import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {testIdProps} from '@github-ui/test-id-props'
import {useSearchParams} from '@github-ui/use-navigate'
import {useEffect, useMemo} from 'react'
import {useQueryLoader} from 'react-relay'

import type {IssuesAndPullRequestsCountSecondaryQuery} from './__generated__/IssuesAndPullRequestsCountSecondaryQuery.graphql'
import {EmptyState} from './EmptyState'
import {IssuesAndPullRequestsCountSecondaryQueryQ} from './IssuesAndPullRequestsCount'
import {LabelListPagination} from './LabelListPagination'
import {LabelRow} from './LabelRow'
import {LabelSortMenu} from './LabelSortMenu'
import type {RepositoryResult} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'

// Not exported until we implement fragment-checks.ts for unused field detection
type LabelListFragment = RelayCompat<
  Pick<RepositoryResult, 'id' | 'nameWithOwner' | 'isWritable' | 'viewerCanPush' | 'labels'>
>

type LabelListProps = {
  repository: LabelListFragment
  onCreateLabel?: () => void
}

export function LabelList({repository, onCreateLabel}: LabelListProps) {
  if (!repository.labels) {
    throw new Error('labels data is null or undefined')
  }

  return <LabelListInternal data={repository} onCreateLabel={onCreateLabel} />
}

function LabelListInternal({data, onCreateLabel}: {data: LabelListFragment; onCreateLabel?: () => void}) {
  const nodes = useMemo(() => {
    // telay types mark description as possibly undefined, but LabelRow expects `string | null`
    // its temporary until LabelList is also migrated off Relay (https://github.com/github/planning-tracking/issues/3053).
    return (
      data.labels?.edges
        ?.map(edge => (edge?.node ? {...edge.node, description: edge.node.description ?? null} : null))
        .filter(node => !!node) || []
    )
  }, [data])
  const [searchParams] = useSearchParams()

  const dataCount = data.labels?.totalCount || 0
  const metadataTitle = <span aria-live="polite">{dataCount === 1 ? `1 label` : `${dataCount} labels`}</span>
  const isActionsAvailable = data.isWritable && data.viewerCanPush

  const actions = useMemo(() => {
    if (nodes.length === 0 || searchParams.get('q')) return []
    else {
      return [
        {
          key: 'label-sort-menu',
          render: () => <LabelSortMenu />,
        },
      ]
    }
  }, [nodes.length, searchParams])

  const listItemsHeader = <ListViewMetadata title={metadataTitle} actions={actions} actionsLabel="Label actions" />

  const [labelIssueAndPrCountRef, loadLabelIssueAndPrCount] = useQueryLoader<IssuesAndPullRequestsCountSecondaryQuery>(
    IssuesAndPullRequestsCountSecondaryQueryQ,
  )

  useEffect(() => {
    const labelIds = nodes.map(node => node.id)
    loadLabelIssueAndPrCount({nodes: labelIds})
  }, [nodes, loadLabelIssueAndPrCount])

  return (
    <div className={styles.labelListWrapper} data-hpc>
      <ListView
        {...testIdProps('repository-label-list-view')}
        title="Labels"
        metadata={listItemsHeader}
        titleHeaderTag="h2"
        // role="status" doesn't add much for the static "no labels in repository" case,
        // but it's useful for the "no matches" case since that message is rendered dynamically after filtering.
        as={nodes.length > 0 ? 'ul' : 'div'}
        role={nodes.length > 0 ? 'list' : 'status'}
      >
        {nodes.length > 0 &&
          nodes.map(node => (
            <LabelRow
              key={node.id}
              label={node}
              isActionsAvailable={isActionsAvailable}
              viewerCanPush={data.viewerCanPush}
              secondaryQueryRef={labelIssueAndPrCountRef}
              repositoryId={data.id}
              repositoryNameWithOwner={data.nameWithOwner}
            />
          ))}
        {nodes.length === 0 && (
          <EmptyState
            noLabelsCreated={dataCount === 0}
            viewerCanPush={data.viewerCanPush}
            onCreateLabel={onCreateLabel}
          />
        )}
      </ListView>
      {nodes.length > 0 && <LabelListPagination dataCount={dataCount} />}
    </div>
  )
}
